import type { PrepSidePlan, SavedDeckEntry } from "@/lib/local-store";
import type { CardRecord } from "@/lib/types";

export type SideCardRole =
  | "removal"
  | "board_wipe"
  | "aggro"
  | "blocker"
  | "value"
  | "heal"
  | "ramp"
  | "slow"
  | "tech";

export type SidePackage = {
  id: string;
  label: string;
  /** Condition libre : "si board wide", "si go 2nd", "si ils ont X" */
  when?: string;
  sideIn: SavedDeckEntry[];
  sideOut: SavedDeckEntry[];
};

export type SideGuide = {
  id: string;
  deckId: string;
  /** Légende adverse (optionnel si tu tags juste un archétype) */
  againstLegendId: string | null;
  againstLegendName: string;
  /** Ex. "Aggro Fury", "Control Mind" */
  againstLabel: string;
  playOrder: "first" | "second" | "both";
  notes: string;
  /** Toujours side out / in, quel que soit le package choisi */
  coreOut: SavedDeckEntry[];
  coreIn: SavedDeckEntry[];
  /** Packages alternatifs : choisir A OU B OU C */
  alternatives: SidePackage[];
  createdAt: string;
  updatedAt: string;
};

export type SideSuggestion = {
  summary: string;
  archetype: string;
  coreOut: SavedDeckEntry[];
  coreIn: SavedDeckEntry[];
  alternatives: Array<Omit<SidePackage, "id"> & { reason: string }>;
};

function makePkgId() {
  return `pkg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function newSidePackage(partial?: Partial<SidePackage>): SidePackage {
  return {
    id: makePkgId(),
    label: partial?.label ?? "Option",
    when: partial?.when ?? "",
    sideIn: partial?.sideIn ?? [],
    sideOut: partial?.sideOut ?? [],
  };
}

function haystack(card: CardRecord) {
  return `${card.name} ${card.cleanName ?? ""} ${card.text ?? ""} ${card.tags.join(" ")} ${(card.type ?? "")}`.toLowerCase();
}

export function classifyCardRoles(card: CardRecord): SideCardRole[] {
  const t = haystack(card);
  const roles: SideCardRole[] = [];
  const energy = card.energy ?? 3;

  if (
    /kill|destroy|defeat|damage|stun|exhaust|banish|remove|pierce|snipe|execute|assassinate|strike/.test(
      t,
    )
  ) {
    roles.push("removal");
  }
  if (/all (units|enemies|opponents)|each unit|board|aoe|sweep|wipe|mass/.test(t)) {
    roles.push("board_wipe");
  }
  if (/heal|shield|guard|tough|defend|protect|ward|regen/.test(t)) {
    roles.push("heal");
  }
  if (/draw|search|look at|scry|discover|tutor/.test(t)) {
    roles.push("value");
  }
  if (/energy|power|rune|ramp|accelerate/.test(t) && /gain|add|\+/.test(t)) {
    roles.push("ramp");
  }
  if (card.type === "Unit" && energy <= 2) roles.push("aggro");
  if (card.type === "Unit" && (card.might ?? 0) >= 3 && energy <= 3) roles.push("blocker");
  if (energy >= 5 || /legendary|ultimate|expensive/.test(t)) roles.push("slow");
  if (/token|recycle|discard|counter|tax|hate|tech/.test(t)) roles.push("tech");

  if (roles.length === 0) {
    if (card.type === "Spell") roles.push("tech");
    else if (card.type === "Unit") roles.push(energy <= 3 ? "aggro" : "slow");
    else roles.push("tech");
  }
  return [...new Set(roles)];
}

function qtyMap(entries: SavedDeckEntry[]) {
  return new Map(entries.map((e) => [e.cardId, e.quantity]));
}

function takeFromPool(
  pool: Array<{ card: CardRecord; quantity: number; roles: SideCardRole[] }>,
  prefer: SideCardRole[],
  copies: number,
  used: Set<string>,
): SavedDeckEntry[] {
  const result: SavedDeckEntry[] = [];
  let remaining = copies;
  const ranked = [...pool]
    .filter((p) => !used.has(p.card.id) && p.quantity > 0)
    .sort((a, b) => {
      const score = (p: (typeof pool)[0]) =>
        prefer.reduce((s, r) => s + (p.roles.includes(r) ? 3 : 0), 0) -
        (p.card.energy ?? 3) * 0.15;
      return score(b) - score(a);
    });

  for (const p of ranked) {
    if (remaining <= 0) break;
    if (!prefer.some((r) => p.roles.includes(r)) && prefer.length > 0) {
      // soft fallback later
      continue;
    }
    const take = Math.min(p.quantity, remaining, prefer.includes("board_wipe") ? p.quantity : 2);
    if (take <= 0) continue;
    result.push({ cardId: p.card.id, quantity: take });
    used.add(p.card.id);
    remaining -= take;
  }

  // Fallback if not enough: any remaining preferred-ish cards
  if (remaining > 0) {
    for (const p of ranked) {
      if (remaining <= 0) break;
      if (used.has(p.card.id)) continue;
      const take = Math.min(p.quantity, remaining, 2);
      result.push({ cardId: p.card.id, quantity: take });
      used.add(p.card.id);
      remaining -= take;
    }
  }

  return result;
}

function inferArchetype(opponent: CardRecord | null, domains: string[]): string {
  const d = new Set(domains.map((x) => x.toLowerCase()));
  const name = (opponent?.name ?? "").toLowerCase();
  if (d.has("fury") || d.has("chaos") || /aggro|rush|burn/.test(name)) return "aggro";
  if (d.has("mind") || d.has("order") || /control|combo/.test(name)) return "control";
  if (d.has("calm") || d.has("body") || /mid|tempo/.test(name)) return "midrange";
  return "inconnu";
}

/**
 * Suggestions automatiques : contre cette légende, side core + packages A/B (OU).
 */
export function suggestSideGuide(input: {
  opponent: CardRecord | null;
  main: Array<{ card: CardRecord; quantity: number }>;
  sideboard: Array<{ card: CardRecord; quantity: number }>;
  playOrder?: "first" | "second" | "both";
}): SideSuggestion {
  const domains = input.opponent?.domains ?? [];
  const archetype = inferArchetype(input.opponent, domains);
  const playOrder = input.playOrder ?? "both";

  const mainPool = input.main
    .filter((e) => e.card.type !== "Rune" && e.card.type !== "Battlefield" && e.card.type !== "Legend")
    .map((e) => ({ ...e, roles: classifyCardRoles(e.card) }));
  const sbPool = input.sideboard.map((e) => ({ ...e, roles: classifyCardRoles(e.card) }));

  const usedIn = new Set<string>();
  const usedOut = new Set<string>();

  let coreOut: SavedDeckEntry[] = [];
  let coreIn: SavedDeckEntry[] = [];
  const alternatives: SideSuggestion["alternatives"] = [];

  if (archetype === "aggro") {
    coreOut = takeFromPool(mainPool, ["slow", "ramp", "value"], 3, usedOut);
    coreIn = takeFromPool(sbPool, ["removal", "blocker", "heal"], 2, usedIn);
    alternatives.push({
      label: "A — Spot removal",
      when: "si menaces ponctuelles",
      reason: "Priorise le kill ciblé contre leurs menaces clés",
      sideIn: takeFromPool(sbPool, ["removal"], 2, usedIn),
      sideOut: takeFromPool(mainPool, ["slow", "value"], 2, usedOut),
    });
    alternatives.push({
      label: "B — Survive le board",
      when: "si ils flood / wide",
      reason: "Wipe + blockers si le board adverse explose",
      sideIn: takeFromPool(sbPool, ["board_wipe", "blocker", "heal"], 3, new Set(usedIn)),
      sideOut: takeFromPool(mainPool, ["aggro", "slow"], 3, new Set(usedOut)),
    });
  } else if (archetype === "control") {
    coreOut = takeFromPool(mainPool, ["aggro", "tech"], 2, usedOut);
    coreIn = takeFromPool(sbPool, ["value", "tech", "slow"], 2, usedIn);
    alternatives.push({
      label: "A — Threat denser",
      when: "si matchup long",
      reason: "Plus de menaces / value pour gagner le late",
      sideIn: takeFromPool(sbPool, ["slow", "value"], 3, usedIn),
      sideOut: takeFromPool(mainPool, ["aggro", "removal"], 3, usedOut),
    });
    alternatives.push({
      label: "B — Pressure clock",
      when: "si tu dois race",
      reason: "Garde de l'aggro + tech hate pour casser leur plan",
      sideIn: takeFromPool(sbPool, ["aggro", "tech", "removal"], 3, new Set(usedIn)),
      sideOut: takeFromPool(mainPool, ["heal", "slow"], 3, new Set(usedOut)),
    });
  } else {
    // midrange / unknown
    coreOut = takeFromPool(mainPool, ["slow", "tech"], 2, usedOut);
    coreIn = takeFromPool(sbPool, ["removal", "value"], 2, usedIn);
    alternatives.push({
      label: "A — Interaction",
      when: "si ils ont des menaces fortes",
      reason: "Plus d'interaction, coupe le gras",
      sideIn: takeFromPool(sbPool, ["removal", "tech"], 2, usedIn),
      sideOut: takeFromPool(mainPool, ["slow", "ramp"], 2, usedOut),
    });
    alternatives.push({
      label: "B — Value game",
      when: "si jeu de ressources",
      reason: "Draw / threats pour gagner le mid-late",
      sideIn: takeFromPool(sbPool, ["value", "slow"], 2, new Set(usedIn)),
      sideOut: takeFromPool(mainPool, ["aggro"], 2, new Set(usedOut)),
    });
  }

  // Go 2nd tweak: prefer defensive package first
  if (playOrder === "second" && alternatives.length > 1) {
    alternatives.reverse();
  }

  const label =
    input.opponent?.name?.replace(/\s*\([^)]*\)\s*$/u, "").trim() || "ce matchup";
  const summary =
    archetype === "aggro"
      ? `Contre ${label} (aggro) : coupe le lent, rentre interaction / survie — puis choisis A ou B.`
      : archetype === "control"
        ? `Contre ${label} (control) : coupe le trop linéaire, rentre value/threats — A ou B selon le plan.`
        : `Contre ${label} : packages flex — A interaction OU B value.`;

  return {
    summary,
    archetype,
    coreOut,
    coreIn,
    alternatives: alternatives.filter((a) => a.sideIn.length > 0 || a.sideOut.length > 0),
  };
}

function mergeEntries(base: SavedDeckEntry[], extra: SavedDeckEntry[]): SavedDeckEntry[] {
  const map = qtyMap(base);
  for (const e of extra) {
    map.set(e.cardId, (map.get(e.cardId) ?? 0) + e.quantity);
  }
  return [...map.entries()]
    .filter(([, q]) => q > 0)
    .map(([cardId, quantity]) => ({ cardId, quantity }));
}

/** Résout un guide + package choisi en plan In/Out unique. */
export function resolveGuidePlan(
  guide: Pick<SideGuide, "coreIn" | "coreOut" | "alternatives">,
  packageId: string | null,
): PrepSidePlan {
  const pkg = packageId ? guide.alternatives.find((a) => a.id === packageId) : null;
  return {
    sideIn: mergeEntries(guide.coreIn, pkg?.sideIn ?? []),
    sideOut: mergeEntries(guide.coreOut, pkg?.sideOut ?? []),
  };
}

export function formatGuideTitle(guide: SideGuide) {
  const vs = guide.againstLabel || guide.againstLegendName || "Matchup";
  const order =
    guide.playOrder === "first" ? "Go 1st" : guide.playOrder === "second" ? "Go 2nd" : "1st/2nd";
  return `Contre ${vs} · ${order}`;
}

export function guideBalanceDelta(plan: PrepSidePlan) {
  const inn = plan.sideIn.reduce((s, e) => s + e.quantity, 0);
  const out = plan.sideOut.reduce((s, e) => s + e.quantity, 0);
  return { in: inn, out, delta: inn - out };
}
